import { useEffect, useRef, useState } from 'react';
import { IconPrinter, IconDownload, IconX } from '@tabler/icons-react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { sanitizeExportColors } from '../../../utils/exportColors';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { useAuthStore } from '../../../store/authStore';
import customerService from '../../../services/sohozJobCustomerService';
import CustomerDataDocument from './CustomerDataDocument';

const FONT_LINK = 'https://fonts.googleapis.com/css2?family=Noto+Sans+Bengali:wght@400;600;700;800&display=swap';

// Photo/signature live on the R2 public domain, which serves no CORS headers.
// A direct fetch() is blocked there, so R2 URLs go through the backend proxy
// (`/api/files/proxy-image`) — the same route the print-media export uses.
const R2_ORIGIN = 'https://files.sohozkaj.com';

// Wait until every <img> inside a node has finished loading (or errored).
const waitForImages = (node) =>
    Promise.all(
        Array.from(node?.querySelectorAll('img') || []).map((img) =>
            img.complete ? Promise.resolve() : new Promise((res) => { img.onload = res; img.onerror = res; })
        )
    );

// Fetch an image URL and return it as a base64 data URL (null if blocked).
// Lets html2canvas embed cross-origin R2 images that it otherwise can't read.
const fetchAsDataUrl = async (url, token) => {
    if (!url || url.startsWith('data:')) return null;
    try {
        const isR2 = url.startsWith(R2_ORIGIN);
        const apiBase = import.meta.env.VITE_API_URL || '';
        const target = isR2
            ? `${apiBase}/api/question-papers/image-proxy?url=${encodeURIComponent(url)}`
            : url;
        const res = await fetch(target, {
            mode: 'cors',
            cache: 'no-cache',
            headers: isR2 && token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) return null;
        const blob = await res.blob();
        return await new Promise((resolve) => {
            const fr = new FileReader();
            fr.onload = () => resolve(fr.result);
            fr.onerror = () => resolve(null);
            fr.readAsDataURL(blob);
        });
    } catch {
        return null;
    }
};

/**
 * Preview / Print / Download-PDF a Sohoz Job customer record.
 *
 * Pass either `data` (a full customer record — used directly) or `customerId`
 * (fetched on open). `action` controls what fires automatically once the document
 * is rendered: 'preview' (default, just show), 'print', or 'pdf'.
 */
export default function CustomerDataModal({ open, onClose, data: dataProp, customerId, action = 'preview' }) {
    const { t } = useTranslation();
    const { token } = useAuthStore();
    const docRef = useRef(null);
    const [data, setData] = useState(dataProp || null);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const autoFired = useRef(false);

    // Keep direct data in sync when provided.
    useEffect(() => { if (dataProp) setData(dataProp); }, [dataProp]);

    // Fetch by id when no direct data was supplied.
    useEffect(() => {
        if (!open || dataProp || !customerId) return;
        let alive = true;
        setLoading(true);
        (async () => {
            try {
                const res = await customerService.get(customerId);
                if (alive) setData(res.data || null);
            } catch {
                if (alive) notifications.show({ title: t('common.error'), message: t('sohozJob.failedToLoad'), color: 'red' });
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [open, customerId, dataProp]);

    // Reset the auto-fire guard each time the modal opens.
    useEffect(() => { if (open) autoFired.current = false; }, [open]);

    const fileBase = (data?.applicant_name_en || data?.applicant_name_bn || 'customer').replace(/[\\/:*?"<>|]/g, '-').trim();

    const handlePrint = async () => {
        const node = docRef.current;
        if (!node) return;
        setBusy(true);
        try {
            await waitForImages(node);
            const iframe = document.createElement('iframe');
            Object.assign(iframe.style, { position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0' });
            document.body.appendChild(iframe);
            const doc = iframe.contentWindow.document;
            doc.open();
            doc.write(
                `<!doctype html><html><head><meta charset="utf-8"><title>${fileBase}</title>` +
                `<link href="${FONT_LINK}" rel="stylesheet">` +
                `<style>@page{size:A4;margin:10mm}html,body{margin:0;padding:0}` +
                `body>div{width:100%!important;box-sizing:border-box!important}` +
                `*{-webkit-print-color-adjust:exact;print-color-adjust:exact}</style>` +
                `</head><body>${node.outerHTML}</body></html>`
            );
            doc.close();
            const fire = () => {
                iframe.contentWindow.focus();
                iframe.contentWindow.print();
                setTimeout(() => { try { document.body.removeChild(iframe); } catch { /* already gone */ } }, 1000);
            };
            // Give fonts/images a beat to settle inside the iframe before printing.
            setTimeout(fire, 500);
        } finally {
            setBusy(false);
        }
    };

    const handleDownloadPdf = async () => {
        const node = docRef.current;
        if (!node) return;
        setBusy(true);
        try {
            await waitForImages(node);
            const scale = 2;
            // Pre-fetch images as data URLs so the (cross-origin) photo/signature
            // get embedded into the html2canvas clone instead of coming out blank.
            // Keyed by source URL, not index: the clone may hold more than one
            // document root, which would shift a positional lookup.
            const srcs = [...new Set(Array.from(node.querySelectorAll('img'), (im) => im.getAttribute('src')))]
                .filter((src) => src && !src.startsWith('data:') && !src.startsWith('blob:'));
            const dataUrls = new Map(
                await Promise.all(srcs.map(async (src) => [src, await fetchAsDataUrl(src, token)]))
            );
            // An unembeddable image renders blank instead of failing loudly, so say so.
            if (srcs.some((src) => !dataUrls.get(src))) {
                notifications.show({ title: t('common.warning'), message: t('sohozJob.imageEmbedFailed'), color: 'yellow' });
            }
            // Raster the rendered page so Bengali text uses the real loaded font
            // (jsPDF's vector text only supports Latin → mojibake for Bangla).
            const canvas = await html2canvas(node, {
                scale, useCORS: true, backgroundColor: '#ffffff',
                onclone: (clonedDoc) => {
                    clonedDoc.querySelectorAll('[data-pdf-root] img').forEach((im) => {
                        const replacement = dataUrls.get(im.getAttribute('src'));
                        if (replacement) im.src = replacement;
                    });
                    // html2canvas can't parse oklch/lab/color() — rewrite any to rgb first.
                    clonedDoc.querySelectorAll('[data-pdf-root]').forEach((r) => sanitizeExportColors(r));
                },
            });

            const pdf = new jsPDF('p', 'mm', 'a4');
            const pageW = pdf.internal.pageSize.getWidth();
            const pageH = pdf.internal.pageSize.getHeight();
            const margin = 8;
            const imgW = pageW - margin * 2;
            const pxPerMm = canvas.width / imgW;
            const pageContentPx = (pageH - margin * 2) * pxPerMm;

            // Candidate break points: the bottom edge (canvas px) of each atomic
            // block, so a page break never slices through a row/card mid-line.
            const nodeTop = node.getBoundingClientRect().top;
            const breaks = Array.from(node.querySelectorAll('[data-pdf-block]'))
                .map((el) => (el.getBoundingClientRect().bottom - nodeTop) * scale)
                .sort((a, b) => a - b);

            let top = 0;
            let first = true;
            while (top < canvas.height - 1) {
                const target = top + pageContentPx;
                let cut;
                if (target >= canvas.height) {
                    cut = canvas.height;
                } else {
                    // Largest safe break that fits on this page; fall back to a hard
                    // cut when a single block is taller than the page.
                    const candidate = breaks.filter((b) => b > top + 10 && b <= target).pop();
                    cut = candidate || target;
                }
                const sliceH = Math.min(cut - top, canvas.height - top);
                const page = document.createElement('canvas');
                page.width = canvas.width;
                page.height = Math.ceil(sliceH);
                const ctx = page.getContext('2d');
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, page.width, page.height);
                ctx.drawImage(canvas, 0, top, canvas.width, sliceH, 0, 0, canvas.width, sliceH);

                if (!first) pdf.addPage();
                pdf.addImage(page.toDataURL('image/jpeg', 0.95), 'JPEG', margin, margin, imgW, sliceH / pxPerMm);
                first = false;
                top += sliceH;
            }
            pdf.save(`${fileBase}.pdf`);
        } catch {
            notifications.show({ title: t('common.error'), message: t('sohozJob.pdfFailed'), color: 'red' });
        } finally {
            setBusy(false);
        }
    };

    // Auto-fire the requested action once the document is ready. For print/pdf the
    // modal stays hidden — run the action, then close.
    useEffect(() => {
        if (!open || loading || !data || autoFired.current) return;
        if (action === 'print' || action === 'pdf') {
            autoFired.current = true;
            let cancelled = false;
            (async () => {
                await new Promise((r) => setTimeout(r, 150)); // let layout/fonts settle
                if (cancelled) return;
                if (action === 'print') await handlePrint();
                else await handleDownloadPdf();
                onClose();
            })();
            return () => { cancelled = true; };
        }
    }, [open, loading, data, action]);

    // Print / PDF: render the document off-screen so no preview modal shows.
    if (action === 'print' || action === 'pdf') {
        return (
            <div aria-hidden style={{ position: 'fixed', left: -100000, top: 0, pointerEvents: 'none' }}>
                {data && <CustomerDataDocument ref={docRef} data={data} t={t} />}
            </div>
        );
    }

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent
                showClose={false}
                className="p-0 overflow-hidden flex flex-col"
                style={{ width: '880px', maxWidth: '95vw', height: '90vh', maxHeight: '90vh' }}
            >
                <DialogTitle className="sr-only">{t('sohozJob.documentTitle')}</DialogTitle>

                {/* Toolbar */}
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 flex-shrink-0">
                    <span className="text-sm font-semibold text-zinc-700 dark:text-zinc-300 truncate">
                        {(() => {
                            const name = data?.applicant_name_en || data?.applicant_name_bn || '';
                            const suffix = [data?.mobile_number, name].filter(Boolean).join(' - ');
                            return `${t('sohozJob.documentTitle')}${suffix ? ` — ${suffix}` : ''}`;
                        })()}
                    </span>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                            onClick={handleDownloadPdf}
                            disabled={busy || loading || !data}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors disabled:opacity-50"
                        >
                            <IconDownload size={14} /> {t('sohozJob.downloadPdf')}
                        </button>
                        <button
                            onClick={handlePrint}
                            disabled={busy || loading || !data}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors disabled:opacity-50"
                        >
                            <IconPrinter size={14} /> {t('sohozJob.print')}
                        </button>
                        <button
                            onClick={onClose}
                            className="w-7 h-7 flex items-center justify-center rounded-lg border border-zinc-200 dark:border-zinc-700 text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                        >
                            <IconX size={14} />
                        </button>
                    </div>
                </div>

                {/* Preview area */}
                <div className="flex-1 overflow-auto bg-zinc-100 dark:bg-zinc-950 p-4 sm:p-6 flex justify-center">
                    {loading ? (
                        <div className="flex items-center justify-center py-24">
                            <span className="w-8 h-8 border-2 border-zinc-300 border-t-primary rounded-full animate-spin" />
                        </div>
                    ) : data ? (
                        <div className="shadow-lg" style={{ height: 'fit-content' }}>
                            <CustomerDataDocument ref={docRef} data={data} t={t} />
                        </div>
                    ) : (
                        <div className="py-24 text-sm text-zinc-500">{t('sohozJob.failedToLoad')}</div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
